import { formatNumber, formatPercent } from "../../components/charts/formatters"
import { formatSocialPerformanceDate, growthRatioToComparisonPercent } from "./socialPerformanceReport"

const ENGAGEMENT_METRICS = [
  { key: "engagementGrowth", label: "Engagement Growth" },
  { key: "newEngagements", label: "Engagements" },
  { key: "engagementRate", label: "Engagement Rate" },
]

const FOLLOWER_METRICS = [
  { key: "followerGrowth", label: "Follower Growth" },
  { key: "newFollowers", label: "New Followers" },
  { key: "totalFollowers", label: "Total Followers" },
]

const ALL_PLATFORMS = ["facebook", "instagram", "linkedin", "twitter", "tiktok"]

const SELF_ENGAGEMENT_COUNT_KEY = "new_post_engagement"
const SELF_ENGAGEMENT_RATE_KEY = "new_engagement_rate"
const COMPETITOR_ENGAGEMENT_COUNT_KEY = "post_engagement"
const COMPETITOR_ENGAGEMENT_RATE_KEY = "engagement_rate"

function engagementMetricKeysForEntity(isYou) {
  if (isYou) {
    return { countKey: SELF_ENGAGEMENT_COUNT_KEY, rateKey: SELF_ENGAGEMENT_RATE_KEY }
  }
  return { countKey: COMPETITOR_ENGAGEMENT_COUNT_KEY, rateKey: COMPETITOR_ENGAGEMENT_RATE_KEY }
}

export function buildCompetitivePerformanceFetchKey(clientId, startDate, endDate, rangeMode) {
  const start = formatSocialPerformanceDate(startDate)
  const end = formatSocialPerformanceDate(endDate)
  const range = rangeMode === "custom" ? "|custom" : ""
  return `${String(clientId ?? "")}|${start}|${end}${range}`
}

/** Query for GET /report/clients/{id}/competitive. */
export function buildClientCompetitiveReportQuery({
  period,
  year,
  month,
  aggregationMode,
  startDate,
  endDate,
  socialNetwork,
  limit = 100,
  after,
} = {}) {
  const params = {
    aggregation_mode: aggregationMode ?? "paid_and_organic",
  }
  if (period === "custom" && startDate && endDate) {
    params.start_date = startDate
    params.end_date = endDate
  } else {
    params.period = period ?? "month"
    if (year != null) params.year = String(year)
    if (month != null) params.month = String(month)
  }
  if (socialNetwork && socialNetwork !== "all") {
    params.social_network = socialNetwork === "instagram" ? "instagram_graph" : socialNetwork
  }
  if (limit != null) params.limit = String(limit)
  if (after) params.after = after
  return new URLSearchParams(params).toString()
}

function parseMetricNum(value) {
  if (value == null || value === "") return null
  const numeric = Number(value)
  return Number.isNaN(numeric) ? null : numeric
}

function isPaidAndOrganicMode(mode) {
  const normalized = String(mode ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
  return normalized === "paid_and_organic" || normalized === "paidandorganic"
}

function reportAggregationMode(report) {
  return report?.aggregation_mode ?? report?.aggregationMode
}

export function extractCompetitiveReportsArray(data) {
  if (Array.isArray(data?.reports)) return data.reports
  if (Array.isArray(data?.data?.reports)) return data.data.reports
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data)) return data
  return []
}

function reportDisplayName(report) {
  return String(
    report?.name ?? report?.reportName ?? report?.entity_name ?? report?.entityName ?? "",
  ).trim()
}

function metricResultHasValues(entry) {
  if (!entry || typeof entry !== "object") return false
  return entry.new_value != null
}

/** Competitive API may return flat metrics or page-performance-style nested buckets. */
function resolveResultsMetricEntry(results, metricKey) {
  if (!results || typeof results !== "object") return null
  const bucket = results[metricKey]
  if (!bucket || typeof bucket !== "object") return null

  if (metricResultHasValues(bucket)) return bucket

  const nested =
    bucket.paid_and_organic ??
    bucket.paidAndOrganic ??
    bucket.paidandorganic
  if (nested && typeof nested === "object" && metricResultHasValues(nested)) return nested

  return null
}

function resolveMetricFromResults(results, metricKey) {
  return resolveResultsMetricEntry(results, metricKey)
}

function reportHasNamedResults(report) {
  const name = reportDisplayName(report)
  if (!name) return false
  const results = report?.results
  if (!results || typeof results !== "object") return false
  return Object.keys(results).some((key) => {
    const entry = resolveResultsMetricEntry(results, key)
    return metricResultHasValues(entry)
  })
}

function reportCompetitiveType(report) {
  return String(report?.competitive_type ?? report?.competitiveType ?? "").toLowerCase()
}

function shouldIncludeCompetitiveReport(report) {
  if (!reportHasNamedResults(report)) return false
  if (reportCompetitiveType(report) === "sub_brand") return false
  return isPaidAndOrganicMode(reportAggregationMode(report))
}

function entityGroupKey(report) {
  const type = reportCompetitiveType(report)
  const name = reportDisplayName(report)
  const entityId = report?.competitor_entity_id ?? report?.competitorEntityId ?? report?.entity_id ?? report?.entityId
  return `${type}|${name}|${entityId ?? "self"}`
}

function mergeCountMetric(reportList, metricKey, connectedPlatforms = []) {
  let newSum = 0
  let prevSum = 0
  let latestSum = 0
  let hasNew = false
  let hasPrev = false
  let hasLatest = false
  let growthSum = 0
  let growthCount = 0
  const platformFilter =
    Array.isArray(connectedPlatforms) && connectedPlatforms.length > 0
      ? new Set(connectedPlatforms)
      : null

  for (const report of reportList) {
    if (platformFilter) {
      const platform = normalizeCompetitivePlatform(report?.social_network ?? report?.socialNetwork)
      if (!platform || !platformFilter.has(platform)) continue
    }
    const entry = resolveMetricFromResults(report?.results, metricKey)
    if (!entry) continue
    const newValue = parseMetricNum(entry.new_value ?? entry.newValue)
    const previousValue = parseMetricNum(entry.previous_value ?? entry.previousValue)
    const latestValue = parseMetricNum(entry.latest_value ?? entry.latestValue)
    const growth = parseMetricNum(entry.growth)
    if (newValue != null) {
      newSum += newValue
      hasNew = true
    }
    if (previousValue != null) {
      prevSum += previousValue
      hasPrev = true
    }
    if (latestValue != null) {
      latestSum += latestValue
      hasLatest = true
    }
    if (growth != null) {
      growthSum += growth
      growthCount += 1
    }
  }

  // Overall growth comes from the metric's growth field only (no new/previous fallback).
  let growth = null
  if (growthCount > 0) {
    growth = growthSum
  }

  return {
    newValue: hasNew ? newSum : null,
    previousValue: hasPrev ? prevSum : null,
    latestValue: hasLatest ? latestSum : null,
    growth,
  }
}

function toEngagementRateRatio(value) {
  const n = parseMetricNum(value)
  if (n == null) return null
  return n > 1 ? n / 100 : n
}

function mergeRateMetric(reportList, metricKey, connectedPlatforms = [], engagementCountKey = SELF_ENGAGEMENT_COUNT_KEY) {
  let weightedNew = 0
  let weightSum = 0

  const platformFilter =
    Array.isArray(connectedPlatforms) && connectedPlatforms.length > 0
      ? new Set(connectedPlatforms)
      : null

  for (const report of reportList) {
    const platform = normalizeCompetitivePlatform(report?.social_network ?? report?.socialNetwork)
    if (platformFilter && platform && !platformFilter.has(platform)) continue

    const entry = resolveMetricFromResults(report?.results, metricKey)
    if (!entry || !metricResultHasValues(entry)) continue

    const rateNew = toEngagementRateRatio(entry.new_value ?? entry.newValue)
    if (rateNew == null) continue

    const reach = parseMetricNum(
      resolveMetricFromResults(report?.results, "reach")?.new_value ??
        resolveMetricFromResults(report?.results, "reach")?.newValue,
    )
    const engagements = parseMetricNum(
      resolveMetricFromResults(report?.results, engagementCountKey)?.new_value ??
        resolveMetricFromResults(report?.results, engagementCountKey)?.newValue,
    )
    const weight = reach > 0 ? reach : engagements > 0 ? engagements : 1
    weightedNew += rateNew * weight
    weightSum += weight
  }

  return {
    newValue: weightSum > 0 ? weightedNew / weightSum : null,
    previousValue: null,
    growth: null,
  }
}

export function formatCompetitiveGrowthDisplay(growth) {
  if (growth == null || Number.isNaN(Number(growth))) {
    return { text: "0%", isPositive: false, isNegative: false }
  }
  const pctPoints = growthRatioToComparisonPercent(growth)
  if (pctPoints == null) return { text: "0%", isPositive: false, isNegative: false }
  const rounded = Math.round(pctPoints * 10) / 10
  if (rounded === 0) return { text: "0%", isPositive: false, isNegative: false }
  const prefix = rounded > 0 ? "+" : ""
  return {
    text: `${prefix}${rounded}%`,
    isPositive: rounded > 0,
    isNegative: rounded < 0,
  }
}

function normalizeCompetitivePlatform(socialNetwork) {
  const normalized = String(socialNetwork ?? "")
    .trim()
    .toLowerCase()
  if (normalized === "instagram_graph" || normalized === "instagram") return "instagram"
  if (normalized === "facebook") return "facebook"
  if (normalized === "linkedin") return "linkedin"
  if (normalized === "twitter" || normalized === "x") return "twitter"
  if (normalized === "tiktok") return "tiktok"
  return null
}

function readCompetitiveMetricEntry(report, metricKey) {
  if (!report) return null
  const entry = resolveMetricFromResults(report?.results, metricKey)
  if (!entry || !metricResultHasValues(entry)) return null
  return {
    newValue: parseMetricNum(entry.new_value ?? entry.newValue),
    previousValue: parseMetricNum(entry.previous_value ?? entry.previousValue),
    latestValue: parseMetricNum(entry.latest_value ?? entry.latestValue),
    growth: parseMetricNum(entry.growth),
  }
}

function buildPlatformMetricCell(raw, display) {
  return {
    raw: raw ?? 0,
    display: display ?? "0",
  }
}

function buildGrowthMetricCell(entry) {
  if (entry?.growth == null) return buildPlatformMetricCell(0, "0%")
  const formatted = formatCompetitiveGrowthDisplay(entry.growth)
  return buildPlatformMetricCell(growthRatioToComparisonPercent(entry.growth), formatted.text)
}

function buildCountMetricCell(entry, field) {
  const value = entry?.[field]
  if (value == null) return buildPlatformMetricCell(0, formatNumber(0))
  return buildPlatformMetricCell(value, formatNumber(value))
}

function buildEngagementRateMetricCell(entry) {
  if (entry?.newValue == null) return buildPlatformMetricCell(0, formatPercent(0, 1))
  const ratio = toEngagementRateRatio(entry.newValue)
  return buildPlatformMetricCell(ratio, formatPercent(ratio, 1))
}

function buildZeroPlatformMetrics() {
  return {
    engagementGrowth: buildPlatformMetricCell(0, "0%"),
    newEngagements: buildPlatformMetricCell(0, formatNumber(0)),
    engagementRate: buildPlatformMetricCell(0, formatPercent(0, 1)),
    followerGrowth: buildPlatformMetricCell(0, "0%"),
    newFollowers: buildPlatformMetricCell(0, formatNumber(0)),
    totalFollowers: buildPlatformMetricCell(0, formatNumber(0)),
  }
}

export function collectConnectedPlatformsFromReports(reports) {
  const list = Array.isArray(reports) ? reports : []
  const active = new Set()
  for (const report of list) {
    if (!shouldIncludeCompetitiveReport(report)) continue
    const platform = normalizeCompetitivePlatform(report?.social_network ?? report?.socialNetwork)
    if (platform) active.add(platform)
  }
  return ALL_PLATFORMS.filter((platform) => active.has(platform))
}

/** Per-channel metrics from one competitive report row (one social_network). */
function buildPlatformMetricMap(report, isYou = false) {
  if (!report) return buildZeroPlatformMetrics()

  const { countKey, rateKey } = engagementMetricKeysForEntity(isYou)
  const engagement = readCompetitiveMetricEntry(report, countKey)
  const engagementRate = readCompetitiveMetricEntry(report, rateKey)
  const followers = readCompetitiveMetricEntry(report, "followers")

  return {
    engagementGrowth: buildGrowthMetricCell(engagement),
    newEngagements: buildCountMetricCell(engagement, "newValue"),
    engagementRate: buildEngagementRateMetricCell(engagementRate),
    followerGrowth: buildGrowthMetricCell(followers),
    newFollowers: buildCountMetricCell(followers, "newValue"),
    totalFollowers: buildCountMetricCell(followers, "latestValue"),
  }
}

function buildEntityPlatformMetrics(reports, connectedPlatforms = [], isYou = false) {
  const byPlatform = {}
  for (const report of reports) {
    const platform = normalizeCompetitivePlatform(report?.social_network ?? report?.socialNetwork)
    if (!platform) continue
    byPlatform[platform] = buildPlatformMetricMap(report, isYou)
  }
  for (const platform of connectedPlatforms) {
    if (!byPlatform[platform]) {
      byPlatform[platform] = buildZeroPlatformMetrics()
    }
  }
  return byPlatform
}

function buildEntityRow(group, connectedPlatforms = []) {
  const meta = group.meta
  const competitiveType = reportCompetitiveType(meta)
  const isYou = competitiveType === "self"
  const isBrandAverage = competitiveType === "brand"
  const { countKey, rateKey } = engagementMetricKeysForEntity(isYou)
  const engagement = mergeCountMetric(group.reports, countKey, connectedPlatforms)
  const engagementRate = mergeRateMetric(
    group.reports,
    rateKey,
    connectedPlatforms,
    countKey,
  )
  const followers = mergeCountMetric(group.reports, "followers", connectedPlatforms)

  const engagementGrowth = formatCompetitiveGrowthDisplay(engagement.growth)
  const followerGrowth = formatCompetitiveGrowthDisplay(followers.growth)

  return {
    brand: reportDisplayName(meta) || "—",
    subBrand: "",
    isYou,
    isBrandAverage,
    competitiveType,
    position: Number(meta?.position) || 0,
    engagementGrowth: engagementGrowth.text,
    engagementGrowthPositive: engagementGrowth.isPositive,
    engagementGrowthNegative: engagementGrowth.isNegative,
    newEngagements: formatNumber(engagement.newValue ?? 0),
    engagementRate: formatPercent(engagementRate.newValue ?? 0, 1),
    engagementRateSort: engagementRate.newValue ?? 0,
    engagementGrowthSort: engagement.growth ?? 0,
    newEngagementsSort: engagement.newValue ?? 0,
    followerGrowth: followerGrowth.text,
    followerGrowthPositive: followerGrowth.isPositive,
    followerGrowthNegative: followerGrowth.isNegative,
    newFollowers: formatNumber(followers.newValue ?? 0),
    totalFollowers: formatNumber(followers.latestValue ?? 0),
    followerNewSort: followers.newValue ?? 0,
    followerGrowthSort: followers.growth ?? 0,
    totalFollowersSort: followers.latestValue ?? 0,
    platformMetrics: buildEntityPlatformMetrics(group.reports, connectedPlatforms, isYou),
  }
}

function rankRows(rows, sortKey) {
  const sorted = [...rows].sort((a, b) => {
    const aValue = a[sortKey]
    const bValue = b[sortKey]
    const aMissing = aValue == null || Number.isNaN(Number(aValue))
    const bMissing = bValue == null || Number.isNaN(Number(bValue))
    if (aMissing && bMissing) return 0
    if (aMissing) return 1
    if (bMissing) return -1
    return Number(bValue) - Number(aValue)
  })
  return sorted.map((row, index) => ({
    ...row,
    rank: index + 1,
    value: row[sortKey],
    vsMedian: "—",
    performance: 0,
    quartile: "",
    industryPercentile: null,
  }))
}

function growthSortValueToPercentPoints(value) {
  return growthRatioToComparisonPercent(value)
}

function formatStrategicGrowthGapPercentPoints(pctPointDelta) {
  if (pctPointDelta == null || Number.isNaN(Number(pctPointDelta))) return "—"
  const rounded = Math.round(Number(pctPointDelta) * 10) / 10
  if (rounded === 0) return "0%"
  const prefix = rounded > 0 ? "+" : ""
  return `${prefix}${rounded}%`
}

function buildStrategicInsight(rows, sortKey, metricLabel) {
  const youRow = rows.find((row) => row.isYou)
  if (!youRow) return ""

  const topRow = rows.find((row) => row.rank === 1)
  const rank1GrowthPct = growthSortValueToPercentPoints(topRow?.[sortKey])
  const userGrowthPct = growthSortValueToPercentPoints(youRow[sortKey])
  const canCompare =
    Number.isFinite(rank1GrowthPct) &&
    Number.isFinite(userGrowthPct) &&
    youRow.rank !== 1 &&
    userGrowthPct < rank1GrowthPct
  const requiredImprovementPct = canCompare ? rank1GrowthPct - userGrowthPct : 0

  if (youRow.rank === 1) {
    return `You rank #1 for ${metricLabel} within your competitive set.`
  }
  if (requiredImprovementPct > 0) {
    return `You rank #${youRow.rank} for ${metricLabel}. A ${formatStrategicGrowthGapPercentPoints(requiredImprovementPct)} improvement would move you to #1.`
  }
  return `You rank #${youRow.rank} for ${metricLabel} within your competitive set.`
}

export function normalizeCompetitivePerformancePayload(data) {
  const raw = extractCompetitiveReportsArray(data)
  return raw.filter((report) => shouldIncludeCompetitiveReport(report))
}

export function buildSocialPerformanceRowsFromCompetitiveReports(reports) {
  const list = Array.isArray(reports) ? reports : []
  const groups = new Map()

  for (const report of list) {
    if (!shouldIncludeCompetitiveReport(report)) continue
    const key = entityGroupKey(report)
    const existing = groups.get(key)
    if (!existing) {
      groups.set(key, { meta: report, reports: [report] })
    } else {
      existing.reports.push(report)
    }
  }

  const connectedPlatforms = collectConnectedPlatformsFromReports(list)
  const entities = Array.from(groups.values()).map((group) => buildEntityRow(group, connectedPlatforms))
  const engagementRows = rankRows(entities, "engagementGrowthSort")
  const growthRows = rankRows(entities, "followerGrowthSort")

  return {
    engagement: {
      metrics: ENGAGEMENT_METRICS,
      metricLabel: "Engagement Growth",
      columnLabels: ["Engagement Growth", "Engagements", "Engagement Rate"],
      connectedPlatforms,
      rows: engagementRows,
      strategicInsight: buildStrategicInsight(
        engagementRows,
        "engagementGrowthSort",
        "engagement growth",
      ),
    },
    growth: {
      metrics: FOLLOWER_METRICS,
      metricLabel: "Follower Growth",
      columnLabels: ["Follower Growth", "Net New Followers", "Followers"],
      connectedPlatforms,
      rows: growthRows,
      strategicInsight: buildStrategicInsight(
        growthRows,
        "followerGrowthSort",
        "follower growth",
      ),
    },
  }
}
